// Figure dialog: the figure image large (S3 FigureImage slot), its caption, page and source clause (the digitised curve count for Operator and Admin only), with links to the page in the PDF and, when the figure has one, to its chart.
import { Link } from "@tanstack/react-router";
import { ChartLineIcon, FileTextIcon } from "lucide-react";

import type { FigureRow } from "@/api/__generated__";
import { useActiveRegion } from "@/app/region";
import { useInternalDetails } from "@/app/useInternalDetails";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { docSearch } from "@/features/curves/charts";
import { FigureImage } from "@/features/source/FigureImage";
import { plural } from "@/lib/format";

export type DialogFigure = Pick<FigureRow, "key" | "page" | "caption" | "clause_number" | "curve_count">;

interface FigureDialogProps {
  docUid: string;
  region: string;
  figure: DialogFigure | null;
  chartKey?: string | null;
  onOpenChange: (open: boolean) => void;
}

export function figureTitle(figure: Pick<FigureRow, "caption" | "page">): string {
  return figure.caption ?? `Figure on page ${String(figure.page)}`;
}

export function FigureDialog({ docUid, region, figure, chartKey = null, onOpenChange }: FigureDialogProps) {
  const internal = useInternalDetails();
  const currentDocUid = useActiveRegion()?.currentDocUid ?? null;
  return (
    <Dialog open={figure !== null} onOpenChange={onOpenChange}>
      {figure ? (
        <DialogContent size="lg" className="w-[min(960px,94vw)]">
          <DialogHeader>
            <DialogTitle>{figureTitle(figure)}</DialogTitle>
            <DialogDescription>
              Page {figure.page}
              {figure.clause_number ? ` · clause ${figure.clause_number}` : ""}
              {internal && figure.curve_count > 0
                ? ` · ${plural(figure.curve_count, "digitised curve")}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <FigureImage
              docUid={docUid}
              figureKey={figure.key}
              alt={figureTitle(figure)}
              className="max-h-[56vh] w-full object-contain"
            />
          </DialogBody>
          <DialogFooter>
            {chartKey === null ? null : (
              <Button asChild variant="outline">
                <Link
                  to="/$region/curves/$chartKey"
                  params={{ region, chartKey }}
                  search={docSearch(docUid, currentDocUid)}
                >
                  <ChartLineIcon aria-hidden />
                  Open chart
                </Link>
              </Button>
            )}
            <Button asChild variant="outline">
              <Link
                to="/$region/library/$docUid/pdf"
                params={{ region, docUid }}
                search={{ page: figure.page }}
              >
                <FileTextIcon aria-hidden />
                Open on page
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
